import { useTranslation } from "react-i18next";
import { Manager } from "../../../Manager";
import {
  fetchPeriods,
  getPeriodStatus,
  hasPeriodMarketingActivities,
  type Period,
} from "../../../../state/periods";
import { supabase } from "../../../../lib/supabase";
import type { SearchParams, SortParams } from "../../../Manager/types";
import { useCallback } from "react";
import { Box, Badge } from "@radix-ui/themes";

const SEARCH_COLUMNS = ["name"];

const STATUS_OPTIONS = [
  { value: "current", labelKey: "status.current" },
  { value: "past", labelKey: "status.past" },
  { value: "future", labelKey: "status.future" },
];

function StatusBadge({
  status,
  t,
}: {
  status: "current" | "past" | "future";
  t: (key: string) => string;
}) {
  const colorMap = {
    current: "green" as const,
    past: "gray" as const,
    future: "blue" as const,
  };
  const labelKeyMap = {
    current: "status.current",
    past: "status.past",
    future: "status.future",
  };

  return <Badge color={colorMap[status]}>{t(labelKeyMap[status])}</Badge>;
}

export function ManagePeriods() {
  const { t } = useTranslation();

  const fetchData = useCallback(
    (
      limit?: number,
      offset?: number,
      searchParams?: SearchParams,
      sortParams?: SortParams,
    ) => fetchPeriods(limit, offset, searchParams, sortParams),
    [],
  );

  const handleSubmit = async (
    data: Record<string, unknown>,
    item: Period | null,
  ) => {
    const periodData = {
      name: data.name as string,
      start_date: data.start_date as string,
      end_date: data.end_date as string,
    };

    if (item) {
      // Update
      const { error } = await supabase
        .from("periods")
        .update(periodData)
        .eq("id", item.id);
      if (error) throw new Error(error.message);
    } else {
      // Create
      const { error } = await supabase.from("periods").insert([periodData]);
      if (error) throw new Error(error.message);
    }
  };

  const handleDelete = async (item: Period) => {
    // Check if there are marketing activities attached
    const hasActivities = await hasPeriodMarketingActivities(item.id);
    if (hasActivities) {
      throw new Error(t("periods.deleteError"));
    }

    const { error } = await supabase.from("periods").delete().eq("id", item.id);
    if (error) throw new Error(error.message);
  };

  const validateForm = (data: Record<string, unknown>): boolean => {
    const name = data.name as string;
    const startDate = data.start_date as string;
    const endDate = data.end_date as string;

    if (!name?.trim()) return false;
    if (!startDate || !endDate) return false;

    // Validate that end_date is after start_date
    if (new Date(endDate) < new Date(startDate)) return false;

    return true;
  };

  return (
    <Box p="4">
      <Manager.Root<Period> title={t("periods.title")} fetchData={fetchData}>
        <Manager.Sort
          columns={[
            { key: "start_date", isDefault: true, defaultOrder: "asc" },
          ]}
        />
        <Manager.Search>
          <Manager.TextSearch
            columns={SEARCH_COLUMNS}
            placeholder={t("periods.searchPlaceholder")}
          />
          <Manager.SelectSearch<{ value: string; labelKey: string }>
            filterKey="status"
            label={t("status.pending")}
            options={STATUS_OPTIONS}
            getOptionLabel={(opt) => t(opt.labelKey)}
            getOptionValue={(opt) => opt.value}
            allLabel={t("periods.allStatus")}
          />
        </Manager.Search>

        <Manager.CreateButton label={t("periods.createPeriod")} />

        <Manager.Table>
          <Manager.Column<Period>
            header={t("periods.name")}
            accessor={(p) => p.name}
          />
          <Manager.Column<Period>
            header={t("periods.startDate")}
            accessor={(p) => p.start_date}
            sortKey="start_date"
          />
          <Manager.Column<Period>
            header={t("periods.endDate")}
            accessor={(p) => p.end_date}
          />
          <Manager.Column<Period>
            header={t("status.current")}
            accessor={(p) => <StatusBadge status={getPeriodStatus(p)} t={t} />}
          />
          <Manager.Actions>
            <Manager.EditAction />
            <Manager.DeleteAction />
          </Manager.Actions>
        </Manager.Table>

        <Manager.FormDialog<Period>
          title={(isEdit) =>
            isEdit ? t("periods.editPeriod") : t("periods.createPeriod")
          }
          description={(isEdit) =>
            isEdit
              ? t("periods.editDescription")
              : t("periods.createDescription")
          }
          onSubmit={handleSubmit}
          validate={validateForm}
        >
          <Manager.TextField
            name="name"
            label={t("periods.name")}
            required
            placeholder={t("periods.namePlaceholder")}
            getValue={(item) => (item as Period).name}
          />
          <Manager.DateField
            name="start_date"
            label={t("periods.startDate")}
            required
            getValue={(item) => (item as Period).start_date}
          />
          <Manager.DateField
            name="end_date"
            label={t("periods.endDate")}
            required
            getValue={(item) => (item as Period).end_date}
          />
        </Manager.FormDialog>

        <Manager.DeleteDialog<Period>
          getItemName={(p) => p.name}
          onDelete={handleDelete}
        />
      </Manager.Root>
    </Box>
  );
}
